<template>
  <div class="vendors-shell">
    <!-- HERO -->
    <section class="vendors-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Distributors</h1>
        <p class="hero-copy">
          Manage wine reps, wholesalers, distributors, auction sources, and vendor contacts.
        </p>
      </div>

      <button @click="openAddPopup" class="primary-action">
        + New Vendor
      </button>
    </section>

    <!-- TOOLBAR -->
    <section class="vendors-toolbar">
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input
          type="text"
          v-model="filters.search"
          placeholder="Search vendor, contact, phone, email..."
        />
      </div>

      <select v-model="filters.active" class="toolbar-select">
        <option value="">All Vendors</option>
        <option value="true">Active</option>
        <option value="false">Inactive</option>
      </select>
    </section>

    <!-- VENDOR CARDS -->
    <section v-if="filteredVendors.length > 0" class="vendor-grid">
      <article
        v-for="vendor in filteredVendors"
        :key="vendor.vendor_id"
        class="vendor-card"
        :class="{ inactive: vendor.active === false }"
      >
        <div class="vendor-main">
          <div class="vendor-title-row">
            <div>
              <p class="label">Vendor / Distributor</p>
              <h2>{{ vendor.vendor_name }}</h2>
            </div>

            <span
              class="status-pill"
              :class="vendor.active === false ? 'status-inactive' : 'status-active'"
            >
              {{ vendor.active === false ? 'Inactive' : 'Active' }}
            </span>
          </div>

          <div class="vendor-details">
            <div>
              <span>Contact</span>
              <strong>{{ vendor.contact_name || '—' }}</strong>
            </div>

            <div>
              <span>Phone</span>
              <strong>{{ vendor.phone || '—' }}</strong>
            </div>

            <div>
              <span>Email</span>
              <strong>
                <a v-if="vendor.email" :href="'mailto:' + vendor.email">
                  {{ vendor.email }}
                </a>
                <template v-else>—</template>
              </strong>
            </div>
          

            <div>
              <span>Website</span>
              <strong>
                <a
                  v-if="vendor.website"
                  :href="formatWebsite(vendor.website)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Site
                </a>
                <template v-else>—</template>
              </strong>
            </div>
          </div>

          <p v-if="vendor.address" class="vendor-note">
            {{ vendor.address }}
          </p>

          <p v-if="vendor.notes" class="vendor-note gold-note">
            {{ vendor.notes }}
          </p>
        </div>

        <div class="card-actions">
          <button @click="openEditPopup(vendor)" class="ghost-action">
            Edit
          </button>

          <button @click="toggleActive(vendor)" class="warning-action">
            {{ vendor.active === false ? 'Activate' : 'Deactivate' }}
          </button>

          <button @click="confirmDelete(vendor)" class="danger-action">
            Delete
          </button>
        </div>
      </article>
    </section>

    <!-- EMPTY STATE -->
    <section v-else class="empty-state">
      <p class="eyebrow">No Vendors</p>
      <h2>No distributors found.</h2>
      <p>Add Galaxy Wine Co., Handcrafted Wines, RNDC, K&amp;L Wines, WineBid, or another source.</p>

      <button @click="openAddPopup" class="primary-action">
        + New Vendor
      </button>
    </section>

    <!-- ADD / EDIT MODAL -->
    <div v-if="showPopup" class="modal-overlay">
      <div class="vendor-modal">
        <div class="modal-topline"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">
              {{ editing ? 'Edit Vendor' : 'New Vendor' }}
            </p>

            <h2>
              {{ editing ? 'Edit Distributor' : 'Add Distributor' }}
            </h2>

            <p>
              Store vendor contact info, websites, notes, and active status.
            </p>
          </div>

          <button type="button" @click="showPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <form @submit.prevent="submitVendor" class="modal-body">
          <section class="form-panel">
            <div class="panel-heading">
              <h3>Vendor Details</h3>
              <p>Basic distributor or wholesaler information.</p>
            </div>

            <div class="form-grid">
              <label>
                <span>Vendor Name</span>
                <input
                  type="text"
                  v-model="currentVendor.vendor_name"
                  required
                  placeholder="Example: Galaxy Wine Co."
                />
              </label>

              <label>
                <span>Contact Name</span>
                <input
                  type="text"
                  v-model="currentVendor.contact_name"
                  placeholder="Sales rep or main contact"
                />
              </label>

              <label>
                <span>Phone</span>
                <input
                  type="text"
                  v-model="currentVendor.phone"
                  placeholder="Phone number"
                />
              </label>

              <label>
                <span>Email</span>
                <input
                  type="email"
                  v-model="currentVendor.email"
                  placeholder="contact@example.com"
                />
              </label>

              <label>
                <span>Website</span>
                <input
                  type="text"
                  v-model="currentVendor.website"
                  placeholder="vendor website"
                />
              </label>

              <label class="active-toggle">
                <span>Status</span>
                <select v-model="currentVendor.active">
                  <option :value="true">Active</option>
                  <option :value="false">Inactive</option>
                </select>
              </label>
            </div>
          </section>

          <section class="form-panel">
            <div class="panel-heading">
              <h3>Address & Notes</h3>
              <p>Optional information for future reference.</p>
            </div>

            <div class="form-grid single">
              <label>
                <span>Address</span>
                <textarea
                  v-model="currentVendor.address"
                  placeholder="Street, city, state, zip..."
                ></textarea>
              </label>

              <label>
                <span>Notes</span>
                <textarea
                  v-model="currentVendor.notes"
                  placeholder="Ordering notes, rep details, payment terms, delivery schedule..."
                ></textarea>
              </label>
            </div>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showPopup = false" class="cancel-action">
              Cancel
            </button>

            <button type="submit" class="primary-action">
              {{ editing ? 'Update Vendor' : 'Save Vendor' }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  data() {
    return {
      vendors: [],
      filters: {
        search: "",
        active: ""
      },
      showPopup: false,
      editing: false,
      currentVendor: this.getBlankVendor()
    };
  },

  computed: {
    filteredVendors() {
      return this.vendors.filter((vendor) => {
        const search = this.filters.search.toLowerCase();

        const matchesSearch =
          !search ||
          String(vendor.vendor_name || "").toLowerCase().includes(search) ||
          String(vendor.contact_name || "").toLowerCase().includes(search) ||
          String(vendor.phone || "").toLowerCase().includes(search) ||
          String(vendor.email || "").toLowerCase().includes(search) ||
          String(vendor.website || "").toLowerCase().includes(search) ||
          String(vendor.notes || "").toLowerCase().includes(search);

        const matchesActive =
          this.filters.active === "" ||
          String(vendor.active) === this.filters.active;

        return matchesSearch && matchesActive;
      });
    }
  },

  mounted() {
    this.fetchVendors();
  },

  methods: {
    getBlankVendor() {
      return {
        vendor_id: null,
        vendor_name: "",
        contact_name: "",
        phone: "",
        email: "",
        address: "",
        notes: "",
        website: "",
        active: true
      };
    },

    async fetchVendors() {
      try {
        const response = await api.get("/vendors");
        this.vendors = response.data || [];
      } catch (error) {
        console.error("❌ Error fetching vendors:", error);
      }
    },

    openAddPopup() {
      this.currentVendor = this.getBlankVendor();
      this.editing = false;
      this.showPopup = true;
    },

    openEditPopup(vendor) {
      this.currentVendor = {
        ...this.getBlankVendor(),
        ...vendor,
        active: vendor.active !== false
      };

      this.editing = true;
      this.showPopup = true;
    },

    async submitVendor() {
      try {
        if (this.editing) {
          await api.put(`/vendors/${this.currentVendor.vendor_id}`, this.currentVendor);
        } else {
          await api.post("/vendors", this.currentVendor);
        }

        this.showPopup = false;
        await this.fetchVendors();
      } catch (error) {
        console.error("❌ Error saving vendor:", error);
        alert("Error saving vendor. Check console for details.");
      }
    },

    async toggleActive(vendor) {
      try {
        const updatedVendor = {
          ...vendor,
          active: vendor.active === false ? true : false
        };

        await api.put(`/vendors/${vendor.vendor_id}`, updatedVendor);
        await this.fetchVendors();
      } catch (error) {
        console.error("❌ Error updating vendor status:", error);
        alert("Error updating vendor status.");
      }
    },

    async confirmDelete(vendor) {
      if (!confirm(`Are you sure you want to delete "${vendor.vendor_name}"?`)) {
        return;
      }

      try {
        await api.delete(`/vendors/${vendor.vendor_id}`);
        await this.fetchVendors();
      } catch (error) {
        console.error("❌ Error deleting vendor:", error);
        alert("Error deleting vendor.");
      }
    },

    formatWebsite(website) {
      if (!website) return "#";
      return website.startsWith("http") ? website : `https://${website}`;
    }
  }
};
</script>

<style scoped>
.vendors-shell {
  min-height: calc(100vh - 80px);
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* ---------- Hero ---------- */

.vendors-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.38);
  backdrop-filter: blur(18px);
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.vendors-hero h1 {
  margin: 0;
  font-size: clamp(40px, 7vw, 72px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 640px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

/* ---------- Toolbar ---------- */

.vendors-toolbar {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 210px;
  gap: 14px;
  margin: 22px 0;
}

.search-wrap,
.toolbar-select {
  min-height: 56px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: rgba(12, 8, 12, 0.7);
  color: #f7efe8;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}

.search-wrap {
  display: flex;
  align-items: center;
  padding: 0 16px;
}

.search-icon {
  color: #c8a66a;
  font-size: 24px;
  margin-right: 10px;
}

.search-wrap input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: #f7efe8;
  font-size: 16px;
}

.search-wrap input::placeholder {
  color: #8f827e;
}

.toolbar-select {
  padding: 0 16px;
  font-size: 15px;
  outline: 0;
}

/* ---------- Cards ---------- */

.vendor-grid {
  display: grid;
  gap: 18px;
}

.vendor-card {
  border-radius: 28px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.3);
}

.vendor-card.inactive {
  opacity: 0.65;
}

.vendor-main {
  padding: 24px;
}

.vendor-title-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.label {
  margin: 0 0 5px;
  color: #9b8f8b;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 900;
}

.vendor-title-row h2 {
  margin: 0;
  font-size: clamp(24px, 4vw, 34px);
  letter-spacing: -0.045em;
  line-height: 1.02;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
}

.status-active {
  background: rgba(47, 170, 118, 0.14);
  color: #76e0aa;
  border: 1px solid rgba(118, 224, 170, 0.28);
}

.status-inactive {
  background: rgba(155, 45, 45, 0.22);
  color: #ffb4a8;
  border: 1px solid rgba(255, 118, 118, 0.24);
}

.vendor-details {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 22px;
}

.vendor-details div {
  min-height: 78px;
  padding: 15px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.vendor-details span {
  display: block;
  color: #978b86;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 900;
}

.vendor-details strong {
  display: block;
  margin-top: 8px;
  font-size: 15px;
  color: #fff8f1;
  overflow-wrap: anywhere;
}

.vendor-details a {
  color: #f1c979;
  text-decoration: none;
}

.vendor-details a:hover {
  text-decoration: underline;
}

.vendor-note {
  margin: 14px 0 0;
  padding: 14px 16px;
  border-radius: 18px;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.gold-note {
  color: #f1c979;
  background: rgba(200, 166, 106, 0.1);
  border-color: rgba(200, 166, 106, 0.18);
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 24px 24px;
}

/* ---------- Buttons ---------- */

.primary-action,
.cancel-action,
.ghost-action,
.warning-action,
.danger-action {
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition:
    transform 0.14s ease,
    box-shadow 0.14s ease,
    background 0.14s ease;
}

.primary-action {
  min-height: 58px;
  padding: 0 26px;
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.cancel-action,
.ghost-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.warning-action {
  background: rgba(200, 166, 106, 0.22);
  border: 1px solid rgba(200, 166, 106, 0.3);
  color: #f1c979;
}

.danger-action {
  background: rgba(155, 45, 45, 0.38);
  border: 1px solid rgba(255, 118, 118, 0.25);
  color: #ffb4a8;
}

.primary-action:hover,
.cancel-action:hover,
.ghost-action:hover,
.warning-action:hover,
.danger-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

/* ---------- Empty ---------- */

.empty-state {
  padding: 38px;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px dashed rgba(255, 255, 255, 0.16);
  color: #cfc2bd;
}

.empty-state h2 {
  margin: 0 0 8px;
  color: #fff8f1;
  letter-spacing: -0.035em;
}

.empty-state p {
  margin: 0 0 18px;
}

/* ---------- Modal ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.vendor-modal {
  width: min(920px, 96vw);
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.11);
  color: #f7efe8;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.modal-topline {
  height: 5px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: clamp(28px, 5vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  overflow-y: auto;
  padding: 22px 28px 28px;
}

.form-panel {
  padding: 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 16px;
}

.panel-heading {
  margin-bottom: 16px;
}

.panel-heading h3 {
  margin: 0;
  font-size: 20px;
  letter-spacing: -0.025em;
}

.panel-heading p {
  margin: 6px 0 0;
  color: #aaa09b;
  font-size: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 14px;
}

.form-grid.single {
  grid-template-columns: 1fr;
}

.vendor-modal label {
  display: grid;
  gap: 7px;
  color: #f7efe8;
}

.vendor-modal label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.vendor-modal input,
.vendor-modal select,
.vendor-modal textarea {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.vendor-modal textarea {
  min-height: 110px;
  padding-top: 14px;
  resize: vertical;
}

.vendor-modal input::placeholder,
.vendor-modal textarea::placeholder {
  color: #766c68;
}

.vendor-modal input:focus,
.vendor-modal select:focus,
.vendor-modal textarea:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
}

.modal-actions .primary-action,
.modal-actions .cancel-action {
  min-width: 140px;
}

/* ---------- Responsive ---------- */

@media (max-width: 1000px) {
  .vendors-toolbar {
    grid-template-columns: 1fr;
  }

  .vendor-details {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .vendors-shell {
    padding: 16px;
  }

  .vendors-hero,
  .vendor-title-row,
  .modal-header {
    flex-direction: column;
  }

  .vendors-hero {
    align-items: stretch;
    padding: 24px;
  }

  .vendor-details {
    grid-template-columns: 1fr;
  }

  .card-actions,
  .modal-actions {
    flex-direction: column;
  }

  .primary-action,
  .cancel-action,
  .ghost-action,
  .warning-action,
  .danger-action {
    width: 100%;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .vendor-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header,
  .modal-body {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>